import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Clock, ChevronRight, ChevronLeft, Target, Trophy } from 'lucide-react';
import { Button } from '../ui/button';
import LiveBadge from '../LiveBadge';
import { getHeroBackgroundUrl } from '../../utils/homeHelpers';
import { parseTeamsWithFlags } from '../../utils/teamFlags';

const AUTO_ADVANCE_MS = 8000;
const SWIPE_THRESHOLD = 50;

function MatchHeroBadge({ content, type }) {
  const parsed = parseTeamsWithFlags(content.teams);
  if (!parsed) return null;

  return (
    <div className="relative w-[220px] h-[220px] flex flex-col items-center justify-center">
      <div className="absolute inset-0 rounded-2xl bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] shadow-2xl shadow-black/40" />
      <div className="relative z-10 flex flex-col items-center gap-2.5 px-5">
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-4xl leading-none">{parsed.home.flag || ''}</span>
          <span className="text-white font-bold text-xs tracking-wide text-center">{parsed.home.name}</span>
        </div>
        <div className="flex items-center gap-2.5 w-full">
          <div className="flex-1 h-px bg-white/10" />
          <span className={`text-[10px] font-black tracking-widest px-2.5 py-0.5 rounded-full ${
            type === 'live' ? 'bg-red-600 text-white' : 'bg-white/10 text-white/60'
          }`}>
            {type === 'live' ? 'LIVE' : 'VS'}
          </span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-4xl leading-none">{parsed.away.flag || ''}</span>
          <span className="text-white font-bold text-xs tracking-wide text-center">{parsed.away.name}</span>
        </div>
        {content.competition_name && (
          <div className="mt-1 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/20">
            <span className="text-emerald-400 text-[10px] font-semibold tracking-wide uppercase">
              {content.competition_name}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function HeroSlideContent({ slide, matchMinutes, showGlow, getTimeUntilKickoff, navigate, watchButtonRef, onPlayVideo }) {
  const { content, type } = slide;

  const handleCTA = () => {
    if (type === 'highlight' && content.source === 'youtube' && content.youtube_video_id) {
      if (onPlayVideo) onPlayVideo(content);
    } else if (type === 'competition' && content.slug) {
      navigate(`/competitions/${content.slug}`);
    } else {
      navigate(`/match/${content.id}`);
    }
  };

  const ctaLabel = type === 'live' ? 'Watch Live'
    : type === 'starting' ? 'Set Reminder'
    : type === 'highlight' ? 'Watch Highlights'
    : type === 'competition' ? 'Explore League'
    : 'Watch Now';

  const ctaClass = type === 'live' ? 'bg-red-600 hover:bg-red-700'
    : type === 'highlight' ? 'bg-yellow-500 hover:bg-yellow-600 text-black'
    : type === 'competition' ? 'bg-emerald-600 hover:bg-emerald-700'
    : 'bg-emerald-600 hover:bg-emerald-700';

  return (
    <>
      <div className="flex items-center gap-3 mb-5" data-testid="hero-status-row">
        {type === 'live' && (
          <>
            <LiveBadge variant="large" className="rounded-full" />
            {matchMinutes[content.id] && (
              <span className="text-white/80 font-bold text-sm">{matchMinutes[content.id]}</span>
            )}
          </>
        )}
        {type === 'starting' && (
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500">
            <Clock className="w-4 h-4 text-black" />
            <span className="text-black font-bold text-sm uppercase tracking-wide">
              {getTimeUntilKickoff(content.start_time)}
            </span>
          </div>
        )}
        {type === 'highlight' && (
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500" data-testid="hero-highlight-badge">
            <Target className="w-4 h-4 text-black" />
            <span className="text-black font-bold text-sm uppercase tracking-wide">Featured Highlight</span>
          </div>
        )}
        {type === 'competition' && (
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-600" data-testid="hero-competition-badge">
            <Trophy className="w-4 h-4 text-white" />
            <span className="text-white font-bold text-sm uppercase tracking-wide">Competition</span>
          </div>
        )}
        {type === 'replay' && (
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-600">
            <span className="text-white font-bold text-sm uppercase tracking-wide">Full Time</span>
          </div>
        )}
        {content.competition_name && type !== 'competition' && (
          <span className="text-emerald-400 font-semibold text-sm tracking-wide">
            {content.competition_name}
          </span>
        )}
      </div>

      {(() => {
        if (type === 'competition') {
          return (
            <div className="mb-6" data-testid="hero-title">
              {content.logo_url && (
                <img
                  src={content.logo_url}
                  alt=""
                  className="w-16 h-16 object-contain mb-4 drop-shadow-lg"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                {content.title}
              </h1>
            </div>
          );
        }
        const parsed = parseTeamsWithFlags(content.teams);
        if (parsed) {
          return (
            <div className="mb-6" data-testid="hero-teams-display">
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
                <div className="flex items-center gap-3">
                  {parsed.home.flag ? (
                    <span className="text-4xl sm:text-5xl lg:text-6xl">{parsed.home.flag}</span>
                  ) : (
                    <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white font-bold text-sm">
                      {parsed.home.name.slice(0, 3).toUpperCase()}
                    </span>
                  )}
                  <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">{parsed.home.name}</span>
                </div>
                <span className="text-xl sm:text-2xl font-medium text-white/40 uppercase">vs</span>
                <div className="flex items-center gap-3">
                  {parsed.away.flag ? (
                    <span className="text-4xl sm:text-5xl lg:text-6xl">{parsed.away.flag}</span>
                  ) : (
                    <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white font-bold text-sm">
                      {parsed.away.name.slice(0, 3).toUpperCase()}
                    </span>
                  )}
                  <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">{parsed.away.name}</span>
                </div>
              </div>
            </div>
          );
        }
        return (
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
            {content.teams || content.title}
          </h1>
        );
      })()}

      <div className="mb-5 max-w-2xl space-y-1" data-testid="hero-subtitle">
        {content.venue && (
          <p className="text-sm text-gray-300 tracking-wide">
            Live from <span className="text-white font-semibold">{content.venue}</span>
          </p>
        )}
        {content.start_time && (
          <p className="text-xs text-gray-400 tracking-wide">
            <span className="text-gray-300">
              {(() => {
                const kickoff = new Date(content.start_time);
                const now = new Date();
                const isToday = kickoff.toDateString() === now.toDateString();
                const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
                const isTomorrow = kickoff.toDateString() === tomorrow.toDateString();
                const timeStr = kickoff.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
                if (isToday) return `Today \u2022 ${timeStr}`;
                if (isTomorrow) return `Tomorrow \u2022 ${timeStr}`;
                return kickoff.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) + ` \u2022 ${timeStr}`;
              })()}
            </span>
          </p>
        )}
        {!content.venue && !content.start_time && (
          <p className="text-sm text-gray-300">
            {type === 'live' ? 'Watch live on AfroSports.'
              : type === 'starting' ? "Don't miss the kickoff."
              : type === 'highlight' ? 'Watch the latest highlights and goals.'
              : type === 'competition' ? (content.description || 'The best of African football.')
              : content.description || 'The best of African football.'}
          </p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          ref={watchButtonRef}
          size="lg"
          className={`text-white font-bold text-base h-12 px-8 rounded-full uppercase tracking-wider hover:scale-105 transition-all ${ctaClass} ${showGlow ? 'animate-pulse shadow-lg shadow-red-500/50 ring-2 ring-red-400 ring-offset-2 ring-offset-[#0a0a0a]' : ''}`}
          onClick={handleCTA}
          data-testid="hero-cta"
        >
          <Play className="w-5 h-5 mr-2" fill="currentColor" />
          {ctaLabel}
        </Button>
      </div>
    </>
  );
}

export function HeroCarousel({ slides, heroRef, watchButtonRef, matchMinutes, showGlow, getTimeUntilKickoff, navigate, onPlayVideo }) {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const timerRef = useRef(null);
  const total = slides.length;

  const bumpProgress = useCallback(() => setProgressKey(k => k + 1), []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (total <= 1) return;
    timerRef.current = setInterval(() => {
      setDirection(1);
      setActive(prev => (prev + 1) % total);
      bumpProgress();
    }, AUTO_ADVANCE_MS);
  }, [total, bumpProgress]);

  const pauseCarousel = useCallback(() => {
    setPaused(true);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const resumeCarousel = useCallback(() => {
    setPaused(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => { resetTimer(); bumpProgress(); return () => clearInterval(timerRef.current); }, [resetTimer, bumpProgress]);

  const goTo = (idx) => { setDirection(idx > active ? 1 : -1); setActive(idx); bumpProgress(); resetTimer(); };
  const goNext = () => { setDirection(1); setActive(prev => (prev + 1) % total); bumpProgress(); resetTimer(); };
  const goPrev = () => { setDirection(-1); setActive(prev => (prev - 1 + total) % total); bumpProgress(); resetTimer(); };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; pauseCarousel(); };
  const onTouchMove = (e) => { touchEndX.current = e.touches[0].clientX; };
  const onTouchEnd = () => {
    const delta = touchStartX.current - touchEndX.current;
    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      if (delta > 0) goNext(); else goPrev();
    }
    resumeCarousel();
  };

  const slide = slides[active];
  if (!slide) return null;

  const variants = {
    enter: (d) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? '-50%' : '50%', opacity: 0 }),
  };

  return (
    <section
      ref={heroRef}
      className="relative min-h-[68vh] flex items-end pb-14 pt-6 overflow-hidden select-none"
      data-testid="hero-section"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseEnter={pauseCarousel}
      onMouseLeave={resumeCarousel}
    >
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={active}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${getHeroBackgroundUrl(slide.content, slide.bgSource)}')` }}
          data-testid="hero-background"
        />
      </AnimatePresence>

      <div
        className="absolute inset-0 z-[1]"
        style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.95), rgba(10,10,10,0.5) 50%, rgba(10,10,10,0.15))' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex items-end justify-between gap-8">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="max-w-3xl"
            >
              <HeroSlideContent
                slide={slide}
                matchMinutes={matchMinutes}
                showGlow={showGlow}
                getTimeUntilKickoff={getTimeUntilKickoff}
                navigate={navigate}
                watchButtonRef={active === 0 ? watchButtonRef : undefined}
                onPlayVideo={onPlayVideo}
              />
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {(slide.type === 'live' || slide.type === 'starting' || slide.type === 'replay') && slide.content.teams && (
              <motion.div
                key={`badge-${active}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="hidden lg:flex flex-col items-center shrink-0"
                data-testid="hero-match-badge"
              >
                <MatchHeroBadge content={slide.content} type={slide.type} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {total > 1 && (
          <div className="flex items-center gap-4 mt-6" data-testid="hero-carousel-controls">
            <div className="flex items-center gap-2">
              {slides.map((s, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`hero-dot-${i}`}
                  className={`transition-all duration-300 rounded-full ${
                    i === active
                      ? 'w-8 h-2 bg-emerald-500'
                      : 'w-2 h-2 bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={goPrev}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                data-testid="hero-prev"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={goNext}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                data-testid="hero-next"
                aria-label="Next slide"
              >
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            </div>
            <span className="text-white/30 text-xs font-medium ml-auto">
              {active + 1} / {total}
            </span>
          </div>
        )}
      </div>

      {total > 1 && (
        <div className="absolute bottom-0 left-0 right-0 z-20 h-[3px] bg-white/[0.08]" data-testid="hero-progress-track">
          <div
            key={progressKey}
            className="h-full bg-emerald-500 rounded-r-full"
            style={{
              animation: `heroProgress ${AUTO_ADVANCE_MS}ms linear forwards`,
              animationPlayState: paused ? 'paused' : 'running',
            }}
            data-testid="hero-progress-fill"
          />
        </div>
      )}
    </section>
  );
}
